import { type FC, useState } from "react";

import { OptionalNote } from "../OptionalNote";

import { CourseGroupingDropdown } from "./CourseGroupingDropdown";

import { COURSE_ID_QUERY_PARAM } from "components/constants";
import { CourseType } from "components/server-types";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { TextField } from "mds/components/TextField";
import { storeApi } from "store/index";
import { toastSuccessMessage } from "utils/alerts";
import { useQueryParamsManager } from "utils/routing";

type AddOrRenameCourseModalProps = {
  course?: CourseType;
  defaultGroupingId?: string | null;
  eventPrefix: string;
  onClose: () => void;
};

export const AddOrRenameCourseModal: FC<AddOrRenameCourseModalProps> = ({
  course,
  defaultGroupingId,
  eventPrefix,
  onClose,
}) => {
  const [code, setCode] = useState<string>(course?.code ?? "");
  const [title, setTitle] = useState<string>(course?.title ?? "");
  const [selectedGroupingId, setSelectedGroupingId] = useState<string | null>(
    defaultGroupingId || null,
  );
  const { setQueryParams } = useQueryParamsManager();

  const handleSubmit = async () => {
    if (!code.trim()) {
      return;
    }

    if (course) {
      await storeApi.courses.partial_update({
        id: course.id,
        code: code.trim(),
        title: title.trim(),
      });
      toastSuccessMessage(t("home.toasts.course_updated"));
    } else {
      const newCourse = await storeApi.courses.create({
        grouping_id: selectedGroupingId,
        code: code.trim(),
        title: title.trim(),
      });
      toastSuccessMessage(t("home.toasts.course_added"));
      setQueryParams({ [COURSE_ID_QUERY_PARAM]: newCourse.id });
    }

    onClose();
  };

  return (
    <div className="mt-8 flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <label className="flex flex-col gap-1">
          <div className="font-medium">{t("home.courses.course_code_label")}</div>
          <TextField
            eventName={`${eventPrefix} / Edit Code`}
            placeholder={t("home.placeholder.course_code")}
            value={code}
            isEditing
            onChange={(e) => setCode(String(e.target.value))}
            onKeyDown={(e) => {
              if (e.key === "Enter" && code.trim()) {
                handleSubmit();
              }
            }}
          />
        </label>
      </div>

      <div className="flex flex-col gap-2">
        <label className="flex flex-col gap-1">
          <div className="flex items-center justify-between font-medium">
            {t("glossary.course_title")}
            <OptionalNote />
          </div>
          <TextField
            eventName={`${eventPrefix} / Edit Title`}
            placeholder={t("home.placeholder.course_title")}
            value={title}
            isEditing
            onChange={(e) => setTitle(String(e.target.value))}
            onKeyDown={(e) => {
              if (e.key === "Enter" && code.trim()) {
                handleSubmit();
              }
            }}
          />
        </label>
      </div>

      {!course && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between font-medium">
            {t("home.courses.add_course_to_grouping_label")}
            <OptionalNote />
          </div>

          <CourseGroupingDropdown
            selectedGroupingId={selectedGroupingId}
            onSelectionChange={setSelectedGroupingId}
          />
        </div>
      )}

      <div className="flex w-full justify-end gap-2">
        <Button kind="white" size="s" title={t("common.cancel")} onClick={onClose}>
          {t("common.cancel")}
        </Button>

        <Button
          disabled={!code.trim()}
          kind="blue"
          size="s"
          title={course ? t("common.save_changes") : t("common.add.course")}
          onClick={handleSubmit}
        >
          {course ? t("common.save_changes") : t("common.add.course")}
        </Button>
      </div>
    </div>
  );
};
